import React, { useState, useMemo, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import '../../../css/GoogleScholar.css';

const normalizeVietnamese = (str) => {
    if (!str) return '';
    return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/[^a-z0-9\s]/g, '').trim();
};

const calculateNameSimilarity = (nameA, nameB) => {
    if (!nameA || !nameB) return 0;
    const cleanAndSplit = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim().split(' ');
    const tokensA = cleanAndSplit(nameA);
    const tokensB = cleanAndSplit(nameB);
    const exactMatches = tokensA.filter(token => tokensB.includes(token));
    if (exactMatches.length === 0) return 0;
    const remainingA = tokensA.filter(token => !exactMatches.includes(token));
    const remainingB = tokensB.filter(token => !exactMatches.includes(token));
    const initialsA = remainingA.map(token => token[0]).join('');
    const initialsB = remainingB.map(token => token[0]).join('');
    const combinedRemainingB = remainingB.join('');
    const combinedRemainingA = remainingA.join('');
    if (initialsA === combinedRemainingB || initialsB === combinedRemainingA || initialsA === initialsB) return 0.95;
    return exactMatches.length / Math.max(tokensA.length, tokensB.length);
};

const findAuthorInArticle = (authorName, authorships) => {
    if (!authorships || !Array.isArray(authorships)) return null;
    let bestMatch = null;
    let bestSimilarity = 0;
    for (const authorship of authorships) {
        const namesToCheck = [authorship.raw_author_name, authorship.author_name].filter(Boolean);
        for (const name of namesToCheck) {
            const similarity = calculateNameSimilarity(authorName, name);
            if (similarity > bestSimilarity && similarity >= 0.5) {
                bestSimilarity = similarity;
                bestMatch = authorship;
            }
        }
    }
    return bestMatch;
};

const inferPositionFromAuthors = (authorName, authorsText) => {
    if (!authorName || !authorsText) return null;
    const parts = authorsText.split(/\s*,\s*/).map(a => a.trim()).filter(Boolean);
    if (!parts.length) return null; if (parts.length === 1) return 'first';
    let bestIdx = -1;
    let bestSim = 0;
    parts.forEach((name, idx) => {
        const sim = calculateNameSimilarity(authorName, name);
        if (sim > bestSim && sim >= 0.5) { bestSim = sim; bestIdx = idx; }
    });
    if (bestIdx === -1) return null;
    if (bestIdx === 0) return 'first';
    if (bestIdx === parts.length - 1) return 'last';
    return 'middle';
};

const getUserIdFromInput = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    try {
        if (trimmed.startsWith('http')) {
            const url = new URL(trimmed);
            return url.searchParams.get('user')?.trim() || trimmed;
        }
    } catch (_) { }
    return trimmed;
};

const parseArticleDate = (dateString) => {
    if (!dateString) return null;
    const str = String(dateString).trim();
    const parts = str.split(/[-/]/);

    const year = parseInt(parts[0], 10);
    const month = parts[1] ? parseInt(parts[1], 10) - 1 : 0;
    const day = parts[2] ? parseInt(parts[2], 10) : 1;

    if (isNaN(year)) return null;
    return new Date(year, month, day);
};

const AllGoogleScholarModal = ({ isOpen, onClose, defaultLink }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [articles, setArticles] = useState([]);

    const [localFilters, setLocalFilters] = useState({ keyword: '', dateFrom: '', dateTo: '', doi: '', corresponding: '', role: '' });
    const [appliedFilters, setAppliedFilters] = useState({ keyword: '', dateFrom: '', dateTo: '', doi: '', corresponding: '', role: '' });

    const [rankingDict, setRankingDict] = useState({});
    const [manualScores, setManualScores] = useState({});

    const [isDesktop, setIsDesktop] = useState(true);
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = () => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    };

    useEffect(() => {
        const timer = setTimeout(() => setAppliedFilters(localFilters), 300);
        return () => clearTimeout(timer);
    }, [localFilters]);

    useEffect(() => {
        if (isOpen) {
            const cachedDict = sessionStorage.getItem('scimagoRankingDict');
            if (cachedDict) {
                setRankingDict(JSON.parse(cachedDict));
                return;
            }

            fetch(`${process.env.REACT_APP_API_BASE_URL}/scimagorankings`, { headers: getAuthHeaders() })
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        const dict = {};
                        data.forEach(item => {
                            if (item.Title && item.Ranking) dict[item.Title.toLowerCase().replace(/[^a-z0-9]/g, '')] = item.Ranking;
                        });
                        setRankingDict(dict);
                        sessionStorage.setItem('scimagoRankingDict', JSON.stringify(dict));
                    }
                })
                .catch(err => console.error(err));
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && defaultLink === 'ALL') {
            const fetchFromBackend = async () => {
                setLoading(true);
                setError('');
                try {
                    // const response = await fetch('http://localhost:5000/api/allpublications'); --> Đường dẫn chạy local
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/allpublications`, {
                        headers: getAuthHeaders()
                    });
                    if (!response.ok) throw new Error('Không thể lấy dữ liệu từ Backend');
                    const backendData = await response.json();

                    if (backendData && backendData.length > 0) {
                        let allArticlesFlattened = [];

                        backendData.forEach(userRecord => {
                            const scholarData = userRecord.Data;

                            if (scholarData && scholarData.articles && Array.isArray(scholarData.articles)) {
                                const userArticles = scholarData.articles.map(a => ({
                                    ...a,
                                    ownerName: userRecord.OwnerName || scholarData.author?.name || 'N/A',
                                    fetchedAuthorName: scholarData.author?.name
                                }));
                                allArticlesFlattened = [...allArticlesFlattened, ...userArticles];
                            }
                        });

                        if (allArticlesFlattened.length > 0) {
                            setArticles(allArticlesFlattened);
                        } else {
                            setError('Đã lấy được dữ liệu, nhưng không tìm thấy bài báo nào bên trong');
                        }
                    } else {
                        setError('Chưa có dữ liệu bài báo trong Hệ thống. Vui lòng kiểm tra lại tiến trình quét ở Backend');
                    }
                } catch (err) {
                    setError('Lỗi kết nối đến máy chủ: ' + err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchFromBackend();
        }
    }, [isOpen, defaultLink]);

    const computeArticleMeta = useCallback((article) => {
        const citations = article.cited_by?.value || 0;
        let authorPosition = null; let isCorresponding = false; let hasDoiData = false;
        const targetAuthorName = article.fetchedAuthorName || article.ownerName;

        if (article.doi && article.data?.authorships && targetAuthorName) {
            const match = findAuthorInArticle(targetAuthorName, article.data.authorships);
            if (match) { authorPosition = match.author_position; isCorresponding = match.is_corresponding === true; hasDoiData = true; }
        }
        if (!hasDoiData && !authorPosition && article.authors && targetAuthorName) {
            authorPosition = inferPositionFromAuthors(targetAuthorName, article.authors);
        }

        let totalAuthors = article.data?.authorships?.length || article.authors?.split(',').length || 0;
        let ranking = '-';
        let candidateNames = [];

        if (article.data?.primary_location?.source?.display_name) candidateNames.push(article.data.primary_location.source.display_name);
        if (article.data?.primary_location?.raw_source_name) candidateNames.push(article.data.primary_location.raw_source_name);
        if (article.publication) {
            let gsPub = article.publication.split(',')[0];
            candidateNames.push(gsPub);
            candidateNames.push(gsPub.replace(/\s+\d+.*$/, ''));
        }

        for (let name of candidateNames) {
            if (!name) continue;
            let cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (rankingDict[cleanName]) { ranking = rankingDict[cleanName]; break; }
        }

        if (ranking === '-' && article.publication) {
            let rawGsName = article.publication.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (rawGsName.length > 10) {
                const sortedKeys = Object.keys(rankingDict).sort((a, b) => b.length - a.length);
                for (let scimagoName of sortedKeys) {
                    if (scimagoName.length > 10 && rawGsName.includes(scimagoName)) {
                        ranking = rankingDict[scimagoName];
                        break;
                    }
                }
            }
        }

        let diemQuyDoi = 0;
        const diemTapChi = (ranking !== '-') ? ranking : (manualScores[article.title] !== undefined ? manualScores[article.title] : (article.journal_score || ''));

        if (diemTapChi && authorPosition && totalAuthors > 0) {
            let multiplier = 0;
            const normalizedDiem = String(diemTapChi).toUpperCase().trim();
            if (normalizedDiem === 'Q1') multiplier = 3;
            else if (normalizedDiem === 'Q2') multiplier = 2;
            else if (normalizedDiem === 'Q3') multiplier = 1.5;
            else if (normalizedDiem === 'Q4' || normalizedDiem === 'SCOPUS') multiplier = 1;
            else { const num = parseFloat(diemTapChi); if (!isNaN(num)) multiplier = num; }

            if (multiplier > 0) {
                const posScore = authorPosition === 'first' ? (1 / 6) : 0;
                const corrScore = isCorresponding ? (1 / 6) : 0;
                const countScore = (2 / 3) / totalAuthors;
                diemQuyDoi = Math.round((posScore + corrScore + countScore) * multiplier * 100) / 100;
            }
        }
        return { citations, authorPosition, isCorresponding, hasDoiData, totalAuthors, ranking, diemQuyDoi };
    }, [rankingDict, manualScores]);

    const processedArticles = useMemo(() => articles.map(a => ({ article: a, meta: computeArticleMeta(a) })), [articles, computeArticleMeta]);

    const filteredArticles = useMemo(() => {
        let result = processedArticles;

        if (appliedFilters.keyword) {
            const kw = normalizeVietnamese(appliedFilters.keyword);
            result = result.filter(item => `${normalizeVietnamese(item.article.title)} ${normalizeVietnamese(item.article.authors)} ${normalizeVietnamese(item.article.publication)} ${normalizeVietnamese(item.article.ownerName)}`.includes(kw));
        }

        if (appliedFilters.dateFrom || appliedFilters.dateTo) {
            result = result.filter(item => {
                const dateStr = item.article.pub_date || item.article.publication_date || item.article.year;
                const articleDate = parseArticleDate(dateStr);

                if (!articleDate) return true;

                let isValid = true;
                if (appliedFilters.dateFrom) {
                    const fromDate = new Date(appliedFilters.dateFrom);
                    fromDate.setHours(0, 0, 0, 0);
                    isValid = isValid && (articleDate >= fromDate);
                }
                if (appliedFilters.dateTo) {
                    const toDate = new Date(appliedFilters.dateTo);
                    toDate.setHours(23, 59, 59, 999);
                    isValid = isValid && (articleDate <= toDate);
                }

                return isValid;
            });
        }

        if (appliedFilters.doi === 'doi') result = result.filter(item => item.article.doi);
        if (appliedFilters.doi === 'no-doi') result = result.filter(item => !item.article.doi);
        if (appliedFilters.corresponding === 'yes') result = result.filter(item => item.meta.isCorresponding);
        if (appliedFilters.corresponding === 'no') result = result.filter(item => !item.meta.isCorresponding);
        if (appliedFilters.role) result = result.filter(item => item.meta.authorPosition === appliedFilters.role);

        return result;
    }, [processedArticles, appliedFilters]);

    const stats = useMemo(() => {
        return filteredArticles.reduce((acc, curr) => {
            acc.citations += curr.meta.citations;
            if (curr.meta.authorPosition === 'first') acc.first++;
            if (curr.meta.isCorresponding) acc.corresponding++;
            acc.totalScore += (curr.meta.diemQuyDoi || 0);
            return acc;
        }, { citations: 0, first: 0, corresponding: 0, totalScore: 0 });
    }, [filteredArticles]);

    const groupedArticles = useMemo(() => {
        const groups = {};
        filteredArticles.forEach(item => {
            const owner = item.article.ownerName || 'N/A';
            if (!groups[owner]) {
                groups[owner] = [];
            }
            groups[owner].push(item);
        });
        return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    }, [filteredArticles]);

    const handleExport = () => {
        if (!groupedArticles || groupedArticles.length === 0) return;

        const rows = [];
        const formulaMap = [];
        let currentRow = 2;

        groupedArticles.forEach(([ownerName, userArticles]) => {
            const startRow = currentRow;

            userArticles.forEach((item, index) => {
                const diemTapChi = (item.meta.ranking !== '-') ? item.meta.ranking : (manualScores[item.article.title] !== undefined ? manualScores[item.article.title] : (item.article.journal_score || ''));

                rows.push({
                    '#': index + 1,
                    'Giáo viên': item.article.ownerName || 'N/A',
                    'Title': item.article.title || '',
                    'Authors': item.article.authors || '',
                    'Total Authors': item.meta.totalAuthors || '',
                    'Year': item.article.pub_date || item.article.publication_date || item.article.year || '',
                    'Publication': item.article.publication || '',
                    'Điểm tạp chí/Kỷ yếu hội thảo': diemTapChi,
                    'Citations': item.meta.citations,
                    'DOI': item.article.doi || '',
                    'Author Position': item.meta.authorPosition || '',
                    'Corresponding': item.meta.isCorresponding ? 'Yes' : 'No',
                    'Điểm quy đổi': ''
                });

                const formula = `IF(OR(H${currentRow}="", K${currentRow}="", L${currentRow}=""), 0, ROUND((IF(K${currentRow}="first", 1/6, 0) + IF(L${currentRow}="Yes", 1/6, 0) + (2/3)/E${currentRow}) * IF(H${currentRow}="Q1", 3, IF(H${currentRow}="Q2", 2, IF(H${currentRow}="Q3", 1.5, IF(OR(H${currentRow}="Q4", H${currentRow}="Scopus"), 1, H${currentRow})))), 2))`;
                formulaMap.push({ cell: `M${currentRow}`, formula: formula });

                currentRow++;
            });

            const endRow = currentRow - 1;

            rows.push({
                '#': '', 'Giáo viên': '', 'Title': '', 'Authors': '', 'Total Authors': '', 'Year': '', 'Publication': '', 'Điểm tạp chí/Kỷ yếu hội thảo': '', 'Citations': '', 'DOI': '', 'Author Position': '',
                'Corresponding': `TỔNG CỦA ${ownerName.toUpperCase()}:`,
                'Điểm quy đổi': ''
            });

            formulaMap.push({ cell: `M${currentRow}`, formula: `SUM(M${startRow}:M${endRow})` });

            rows.push({
                '#': '', 'Giáo viên': '', 'Title': '', 'Authors': '', 'Total Authors': '', 'Year': '', 'Publication': '', 'Điểm tạp chí/Kỷ yếu hội thảo': '', 'Citations': '', 'DOI': '', 'Author Position': '', 'Corresponding': '', 'Điểm quy đổi': ''
            });

            currentRow += 2;
        });

        const subtotalCells = formulaMap.filter(f => f.formula.startsWith('SUM')).map(f => f.cell);

        rows.push({
            '#': '', 'Giáo viên': '', 'Title': '', 'Authors': '', 'Total Authors': '', 'Year': '', 'Publication': '', 'Điểm tạp chí/Kỷ yếu hội thảo': '', 'Citations': '', 'DOI': '', 'Author Position': '',
            'Corresponding': 'TỔNG CỘNG TOÀN TRƯỜNG:',
            'Điểm quy đổi': ''
        });

        if (subtotalCells.length > 0) {
            formulaMap.push({ cell: `M${currentRow}`, formula: `SUM(${subtotalCells.join(',')})` });
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);

        formulaMap.forEach(item => {
            worksheet[item.cell] = { t: 'n', f: item.formula };
        });

        const noteRow = currentRow + 2;
        worksheet[`A${noteRow}`] = {
            t: 's',
            v: '1. Nhập dữ liệu tại Cột H (Điểm tạp chí):\nBài báo quốc tế: Hệ thống tự động điền Q1-Q4. Nếu trống, hãy nhập Q1-Q4, Scopus hoặc điểm số.\nBài báo trong nước: Nhập trực tiếp số điểm.\n2. Cột K (Author Position): Nhập first, middle hoặc last.\n3. Cột L (Corresponding): Yes hoặc No.',
        };

        if (worksheet['!ref']) {
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            range.e.r = Math.max(range.e.r, noteRow);
            worksheet['!ref'] = XLSX.utils.encode_range(range);
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Publications');
        XLSX.writeFile(workbook, 'ToanTruong_GoogleScholar.xlsx');
    };

    if (!isOpen) return null;

    const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
    const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' };

    const getRankingBadgeStyle = (ranking) => {
        const baseStyle = { padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' };
        switch (ranking) {
            case 'Q1': return { ...baseStyle, backgroundColor: '#22c55e', color: 'white' };
            case 'Q2': return { ...baseStyle, backgroundColor: '#eab308', color: 'white' };
            case 'Q3': return { ...baseStyle, backgroundColor: '#f97316', color: 'white' };
            case 'Q4': return { ...baseStyle, backgroundColor: '#ef4444', color: 'white' };
            default: return { ...baseStyle, backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: 'normal' };
        }
    };

    return (
        <div className="scholar-modal-overlay">
            <div className="scholar-modal-box">
                <button className="scholar-close-btn" onClick={onClose}>&times;</button>
                <div className="scholar-container">
                    <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>Dữ Liệu Google Scholar Toàn Trường</h1>

                    {loading && <div style={{ textAlign: 'center', padding: '50px' }}><i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: '30px', color: '#0284c7' }}></i><p>Đang tải dữ liệu từ máy chủ...</p></div>}
                    {error && <div style={{ color: '#991b1b', background: '#fee2e2', padding: '14px', borderRadius: '12px', textAlign: 'center', margin: '20px 0' }}>{error}</div>}

                    {!loading && articles.length > 0 && (
                        <div className="results-section">
                            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', justifyContent: 'space-between', alignItems: isDesktop ? 'flex-start' : 'stretch', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'center' : 'stretch', gap: '15px' }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', textAlign: isDesktop ? 'left' : 'center' }}><i className="fa-regular fa-file-lines"></i> Báo Cáo Tổng Hợp</h3>
                                    <button
                                        style={{ padding: '8px 16px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#0f172a', border: '1px solid #cbd5e1', display: 'flex', justifyContent: 'center', alignItems: 'center', width: isDesktop ? 'auto' : '100%' }}
                                        onClick={handleExport}
                                    >
                                        <i className="fa-solid fa-download" style={{ color: '#2563eb', marginRight: '6px' }}></i> Xuất Excel
                                    </button>
                                </div>

                                <div className="stats" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: isDesktop ? 'flex-start' : 'center' }}>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 24px', textAlign: 'center', minWidth: '90px', flex: isDesktop ? 'none' : '1 1 40%' }}>
                                        <div style={{ fontSize: '24px', color: '#e20b0b', fontWeight: 'bold' }}>{Math.round(stats.totalScore * 100) / 100}</div>
                                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>Tổng Điểm</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 24px', textAlign: 'center', minWidth: '90px', flex: isDesktop ? 'none' : '1 1 40%' }}>
                                        <div style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{filteredArticles.length}</div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>Tổng bài báo</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 24px', textAlign: 'center', minWidth: '90px', flex: isDesktop ? 'none' : '1 1 40%' }}>
                                        <div style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.citations}</div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>Tổng trích dẫn</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 24px', textAlign: 'center', minWidth: '90px', flex: isDesktop ? 'none' : '1 1 40%' }}>
                                        <div style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.first}</div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>First Author</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 24px', textAlign: 'center', minWidth: '90px', flex: isDesktop ? 'none' : '1 1 40%' }}>
                                        <div style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.corresponding}</div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>Corresponding</div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '35px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '2fr 1fr 1fr 1.2fr 1.2fr 1.2fr' : '1fr', gap: '15px', alignItems: 'end' }}>
                                    <div><label style={labelStyle}>Từ khóa</label><input style={inputStyle} placeholder="Tiêu đề, tác giả" onChange={e => setLocalFilters({ ...localFilters, keyword: e.target.value })} value={localFilters.keyword} /></div>
                                    <div><label style={labelStyle}>Từ ngày</label><input type="date" style={inputStyle} onChange={e => setLocalFilters({ ...localFilters, dateFrom: e.target.value })} value={localFilters.dateFrom} /></div>
                                    <div><label style={labelStyle}>Đến ngày</label><input type="date" style={inputStyle} onChange={e => setLocalFilters({ ...localFilters, dateTo: e.target.value })} value={localFilters.dateTo} /></div>
                                    <div><label style={labelStyle}>DOI</label><select style={inputStyle} onChange={e => setLocalFilters({ ...localFilters, doi: e.target.value })} value={localFilters.doi}><option value="">Tất cả</option><option value="doi">Có</option><option value="no-doi">Không</option></select></div>
                                    <div><label style={labelStyle}>Corres</label><select style={inputStyle} onChange={e => setLocalFilters({ ...localFilters, corresponding: e.target.value })} value={localFilters.corresponding}><option value="">Tất cả</option><option value="yes">Có</option><option value="no">Không</option></select></div>
                                    <div><label style={labelStyle}>Vai trò</label><select style={inputStyle} onChange={e => setLocalFilters({ ...localFilters, role: e.target.value })} value={localFilters.role}><option value="">Tất cả</option><option value="first">First</option><option value="middle">Middle</option><option value="last">Last</option></select></div>
                                </div>
                                <button
                                    style={{ marginTop: '15px', padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: isDesktop ? 'auto' : '100%' }}
                                    onClick={() => setLocalFilters({ keyword: '', dateFrom: '', dateTo: '', doi: '', corresponding: '', role: '' })}
                                >
                                    Xóa lọc
                                </button>
                            </div>

                            {groupedArticles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#64748b', fontSize: '16px' }}>Không có bài báo nào phù hợp với bộ lọc.</div>
                            ) : (
                                groupedArticles.map(([ownerName, userArticles]) => {
                                    const userTotalScore = userArticles.reduce((sum, item) => sum + (item.meta.diemQuyDoi || 0), 0);

                                    return (
                                        <div key={ownerName} style={{ marginBottom: '40px', border: '1px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                                            <div style={{ backgroundColor: '#f8fafc', padding: '15px 20px', borderBottom: '2px solid #cbd5e1', display: 'flex', flexDirection: isDesktop ? 'row' : 'column', justifyContent: 'space-between', alignItems: isDesktop ? 'center' : 'flex-start', gap: '10px' }}>
                                                <h3 style={{ margin: 0, color: '#0369a1', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <i className="fa-solid fa-user-tie"></i>
                                                    {ownerName}
                                                </h3>
                                                <span style={{ fontWeight: 'bold', color: '#475569', fontSize: '14px', backgroundColor: '#e2e8f0', padding: '4px 10px', borderRadius: '20px' }}>
                                                    Bài báo: <span style={{ color: '#4f46e5' }}>{userArticles.length}</span>
                                                </span>
                                            </div>

                                            <div className="table-container" style={{ overflowX: 'auto', margin: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
                                                <table className="scholar-table" style={{ minWidth: '1300px', borderBottom: 'none', marginBottom: 0 }}>
                                                    <thead>
                                                        <tr>
                                                            <th width="3%">STT</th>
                                                            <th width="20%">Tiêu đề</th>
                                                            <th width="15%">Tác giả</th>
                                                            <th width="5%" style={{ textAlign: 'center' }}>Số TG</th>
                                                            <th width="8%" style={{ textAlign: 'center' }}>Ngày XB</th>
                                                            <th width="15%">Tạp chí/Hội nghị</th>
                                                            <th width="8%" style={{ textAlign: 'center' }}>Điểm TC/KY</th>
                                                            <th width="5%" style={{ textAlign: 'center' }}>Trích dẫn</th>
                                                            <th width="8%">DOI</th>
                                                            <th width="8%" style={{ textAlign: 'center' }}>Author Pos</th>
                                                            <th width="8%" style={{ textAlign: 'center' }}>Corres</th>
                                                            <th width="8%" style={{ textAlign: 'center', backgroundColor: '#fef2f2', color: '#991b1b' }}>Điểm QĐ</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {userArticles.map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td style={{ maxWidth: '250px' }}><a href={item.article.link} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', fontWeight: 'bold', textDecoration: 'none' }}>{item.article.title}</a></td>
                                                                <td style={{ fontSize: '13px', color: '#64748b', maxWidth: '150px' }}>{item.article.authors}</td>
                                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.meta.totalAuthors || '-'}</td>
                                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.article.pub_date || item.article.publication_date || item.article.year || '-'}</td>
                                                                <td style={{ fontSize: '13px', color: '#64748b' }}>{item.article.publication || '-'}</td>

                                                                <td style={{ textAlign: 'center' }}>
                                                                    {item.meta.ranking !== '-' ? (
                                                                        <span style={getRankingBadgeStyle(item.meta.ranking)}>{item.meta.ranking}</span>
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Nhập điểm"
                                                                            value={manualScores[item.article.title] !== undefined ? manualScores[item.article.title] : (item.article.journal_score || '')}
                                                                            onChange={(e) => setManualScores(prev => ({ ...prev, [item.article.title]: e.target.value }))}
                                                                            style={{ width: '60px', padding: '4px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px', outline: 'none' }}
                                                                        />
                                                                    )}
                                                                </td>

                                                                <td style={{ textAlign: 'center' }}><span className="citation-badge">{item.meta.citations}</span></td>

                                                                <td style={{ fontSize: '12px' }}>{item.article.doi ? <a href={`https://doi.org/${item.article.doi}`} target="_blank" rel="noreferrer">{item.article.doi}</a> : '-'}</td>

                                                                <td style={{ textAlign: 'center' }}>
                                                                    {item.meta.authorPosition ? (
                                                                        <span className={`role-badge ${item.meta.authorPosition}`}>
                                                                            {item.meta.authorPosition.charAt(0).toUpperCase() + item.meta.authorPosition.slice(1)}
                                                                        </span>
                                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                                </td>

                                                                <td style={{ textAlign: 'center' }}>
                                                                    {item.article.doi ? (
                                                                        item.meta.hasDoiData ? (
                                                                            item.meta.isCorresponding ? <span className="role-badge corresponding">✓ Yes</span> : <span className="role-badge none">✗ No</span>
                                                                        ) : <span style={{ color: '#999' }}>-</span>
                                                                    ) : <span style={{ color: '#999' }}>-</span>}
                                                                </td>

                                                                <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#b91c1c', backgroundColor: '#fef2f2' }}>{item.meta.diemQuyDoi > 0 ? item.meta.diemQuyDoi : '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                                                            <td colSpan="11" style={{ textAlign: 'right', fontWeight: 'bold', paddingRight: '20px', fontSize: '15px' }}>TỔNG CỘNG:</td>
                                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e20b0b', fontSize: '18px', backgroundColor: '#fee2e2' }}>{Math.round(userTotalScore * 100) / 100}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllGoogleScholarModal;